import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Users, CheckCircle, XCircle, Clock, Link2, Copy, Plus, Loader2, Mail
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MemberRegistrations = () => {
  const { societyId } = useParams();
  const [society, setSociety] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [inviteForm, setInviteForm] = useState({
    max_uses: 0,
    expires_days: 7
  });

  useEffect(() => {
    if (societyId) {
      fetchData();
    }
  }, [societyId]);

  const fetchData = async () => {
    try {
      const [socRes, regsRes, invitesRes] = await Promise.all([
        fetch(`${API}/societies/${societyId}`, { credentials: "include" }),
        fetch(`${API}/societies/${societyId}/registrations`, { credentials: "include" }),
        fetch(`${API}/societies/${societyId}/invites`, { credentials: "include" })
      ]);

      if (socRes.ok) setSociety(await socRes.json());
      if (regsRes.ok) setRegistrations(await regsRes.json());
      if (invitesRes.ok) setInvites(await invitesRes.json());
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const createInvite = async () => {
    setCreating(true);
    try {
      const res = await fetch(
        `${API}/societies/${societyId}/invites?max_uses=${inviteForm.max_uses}&expires_days=${inviteForm.expires_days}`,
        { method: "POST", credentials: "include" }
      );

      if (res.ok) {
        const result = await res.json();
        toast.success("Invite created!");
        navigator.clipboard.writeText(result.registration_url);
        toast.info("Registration link copied to clipboard");
        setDialogOpen(false);
        fetchData();
      } else {
        toast.error("Failed to create invite");
      }
    } catch (error) {
      toast.error("Failed to create invite");
    } finally {
      setCreating(false);
    }
  };

  const handleApprove = async (registrationId) => {
    setProcessing(registrationId);
    try {
      const res = await fetch(
        `${API}/societies/${societyId}/registrations/${registrationId}/approve`,
        { method: "PUT", credentials: "include" }
      );

      if (res.ok) {
        toast.success("Member approved!");
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to approve");
      }
    } catch (error) {
      toast.error("Failed to approve");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (registrationId) => {
    setProcessing(registrationId);
    try {
      const res = await fetch(
        `${API}/societies/${societyId}/registrations/${registrationId}/reject`,
        { method: "PUT", credentials: "include" }
      );

      if (res.ok) {
        toast.success("Registration rejected");
        fetchData();
      } else {
        toast.error("Failed to reject");
      }
    } catch (error) {
      toast.error("Failed to reject");
    } finally {
      setProcessing(null);
    }
  };

  const copyLink = (invite) => {
    const url = `${window.location.origin}/register/${societyId}?code=${invite.invite_code}`;
    navigator.clipboard.writeText(url);
    toast.success("Registration link copied!");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-emerald-100 text-emerald-700">Approved</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700">Pending</Badge>;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  const pendingCount = registrations.filter(r => r.status === "pending").length;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Member Registrations
            </h1>
            <p className="text-slate-600">{society?.name}</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800">
                <Link2 className="w-4 h-4 mr-2" />
                Create Invite Link
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Invite Link</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="max_uses">Maximum Uses (0 = unlimited)</Label>
                  <Input
                    id="max_uses"
                    type="number"
                    value={inviteForm.max_uses}
                    onChange={(e) => setInviteForm({ ...inviteForm, max_uses: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="expires_days">Expires After (days)</Label>
                  <Input
                    id="expires_days"
                    type="number"
                    value={inviteForm.expires_days}
                    onChange={(e) => setInviteForm({ ...inviteForm, expires_days: parseInt(e.target.value) || 7 })}
                  />
                </div>
                <Button onClick={createInvite} className="w-full bg-emerald-700 hover:bg-emerald-800" disabled={creating}>
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Invite
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Open Registration Link */}
        <Card className="bg-blue-50 border-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-blue-900">Open Registration Link</p>
                <p className="text-sm text-blue-700">Anyone can register (requires approval)</p>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/register/${societyId}`);
                  toast.success("Open registration link copied!");
                }}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Invite Codes */}
        {invites.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Active Invite Codes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {invites.filter(i => i.is_active).map((invite) => (
                  <div key={invite.invite_code} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <code className="font-mono bg-white px-2 py-1 rounded">{invite.invite_code}</code>
                      <span className="text-sm text-slate-500 ml-3">
                        Used: {invite.uses}/{invite.max_uses || '∞'}
                      </span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => copyLink(invite)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending Registrations Alert */}
        {pendingCount > 0 && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-600" />
              <p className="text-amber-800">
                <strong>{pendingCount}</strong> registration{pendingCount > 1 ? 's' : ''} pending approval
              </p>
            </CardContent>
          </Card>
        )}

        {/* Registrations Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Registrations</CardTitle>
          </CardHeader>
          <CardContent>
            {registrations.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No registrations yet</p>
                <p className="text-sm text-slate-400">Share the registration link to invite members</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Flat</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {registrations.map((reg) => (
                    <TableRow key={reg.registration_id}>
                      <TableCell className="font-medium">{reg.name}</TableCell>
                      <TableCell>{reg.email}</TableCell>
                      <TableCell>{reg.flat_number}</TableCell>
                      <TableCell>{getStatusBadge(reg.status)}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(reg.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {reg.status === "pending" && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-emerald-600 hover:bg-emerald-50"
                              onClick={() => handleApprove(reg.registration_id)}
                              disabled={processing === reg.registration_id}
                            >
                              {processing === reg.registration_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleReject(reg.registration_id)}
                              disabled={processing === reg.registration_id}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
